import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useGames, useTheme } from '@/hooks';
import { useDeviceType } from '@/utils/device';
import { Sidebar, DesktopHeader, DesktopGameGrid } from '@/components/desktop';
import { LoadingSpinner, ErrorState, SearchBox, MobileFooter } from '@/components/common';
import { MobileGameGrid } from '@/components/mobile';
import { ChevronLeft, Sun, Moon } from 'lucide-react';
import { useSiteSettings } from '@/contexts/settings-context';
import styles from './games.module.css';

export const GamesPage: React.FC = () => {
  const navigate = useNavigate();
  const deviceType = useDeviceType();
  const isMobile = deviceType === 'mobile';
  const { theme, toggleTheme } = useTheme();
  const { features } = useSiteSettings();

  const {
    data: gamesResult,
    isLoading,
    error,
    refetch,
  } = useGames();

  const gameList = gamesResult?.data?.games ?? [];

  const handleSearch = (query: string) => {
    navigate(`/search?q=${encodeURIComponent(query)}`);
  };

  // Mobile Layout
  if (isMobile) {
    return (
      <div className={styles.mobileContainer}>
        <header className={styles.mobileHeader}>
          <div className={styles.headerRow}>
            <button className={styles.backBtn} onClick={() => navigate(-1)} title="返回">
              <ChevronLeft size={22} />
            </button>
            <div className={styles.pageTitle}>全部游戏</div>
            {features.darkMode && (
              <button className={styles.themeToggle} onClick={toggleTheme} title={theme === 'dark' ? '切换亮色模式' : '切换暗色模式'}>
                {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
              </button>
            )}
          </div>
          <SearchBox
            onSearch={handleSearch}
            placeholder="搜索游戏..."
            showSuggestions={false}
          />
        </header>

        <main className={styles.mobileMain}>
          {isLoading ? (
            <LoadingSpinner text="加载中..." />
          ) : error ? (
            <ErrorState
              title="加载失败"
              message="无法加载游戏列表"
              onRetry={() => refetch()}
            />
          ) : (
            <MobileGameGrid games={gameList} title="" />
          )}
        </main>
        <MobileFooter />
      </div>
    );
  }

  // Desktop Layout
  return (
    <div className={styles.desktopContainer}>
      <Sidebar activePath="/games" />

      <div className={styles.desktopContent}>
        <DesktopHeader />

        <main className={styles.desktopMain}>
          {isLoading ? (
            <LoadingSpinner text="加载中..." />
          ) : error ? (
            <ErrorState
              title="加载失败"
              message="无法加载游戏列表"
              onRetry={() => refetch()}
            />
          ) : (
            <DesktopGameGrid
              games={gameList}
              title="全部游戏"
              isLoading={isLoading}
            />
          )}
        </main>
      </div>
    </div>
  );
};

export default GamesPage;
